<template>
	<div class="c-sidebar">
		<div :class="['c-sidebar__cover overlay', { active: hasBackground }]"></div>
		<div class="c-sidebar__inner">
			<div class="c-sidebar__header">
				<div class="logo">
					<div class="logo__inner">
						<div class="img-container">
							<img src="/icons/128.png" />
							<span
								:class="['status', realtimeConnectionState]"
								v-b-tooltip.hover
								:title="statusTitle"
							>
							</span>
						</div>
					</div>
					<span class="heading">
						<span> UnderlineMe </span>
						<span> v{{ version }} </span>
					</span>
				</div>
				<b-dropdown id="profile-dropdown" no-caret variant="none" size="sm">
					<template v-slot:button-content>
						<div class="img-container">
							<img :src="avatar" />
							<span
								v-if="isTrial"
								class="status"
								v-b-tooltip.hover
								:title="userTitle"
							></span>
						</div>
						&nbsp;<chevron-down-icon size="1.25x"> </chevron-down-icon>
					</template>
					<b-dropdown-item @click="$bvModal.show('modal-profile')"
						>Profile
						<span v-if="isTrial" class="badge">Upgrade</span>
					</b-dropdown-item>
					<b-dropdown-item @click="$bvModal.show('modal-about')"
						>About</b-dropdown-item
					>
					<b-dropdown-item @click="onLogout">Logout</b-dropdown-item>
				</b-dropdown>
				<a
					href="#"
					:class="['btn btn-toggle', { active: sidebarState === true }]"
					@click.prevent="$store.dispatch('dashboard/toggleSidebar')"
					><chevrons-left-icon size="1.25x"></chevrons-left-icon
				></a>
			</div>
			<div class="c-sidebar__body">
				<div class="c-list">
					<a class="c-list__item interactive" @click="gotoHome">
						<home-icon size="1x" class="c-list__icon"></home-icon
						><span class="c-list__content">All notes</span>
					</a>
				</div>
				<slot> </slot>
			</div>
			<div class="c-sidebar__footer">
				<a
					href="#"
					@click="$bvModal.show('modal-bookmark')"
					class="btn btn-link btn-primary"
				>
					<bookmark-icon size="1.25x"></bookmark-icon>
					&nbsp;
					<span> Bookmark a Link </span>
				</a>
				<router-link to="settings" class="btn btn-link">
					<settings-icon size="1.25x"></settings-icon>
					&nbsp;
					<span> Settings </span>
				</router-link>
				<router-link to="/guide" class="btn btn-link">
					<help-circle-icon size="1.25x"></help-circle-icon>
					&nbsp;
					<span> FAQ & Shortcuts </span>
				</router-link>
			</div>
		</div>
	</div>
</template>

<script>
import {
	ChevronDownIcon,
	ChevronsLeftIcon,
	HelpCircleIcon,
	SettingsIcon,
	HomeIcon,
	BookmarkIcon,
} from 'vue-feather-icons';
export default {
	components: {
		HomeIcon,
		ChevronDownIcon,
		ChevronsLeftIcon,
		HelpCircleIcon,
		SettingsIcon,
		BookmarkIcon,
	},
	computed: {
		sidebarState: function () {
			return this.$store.getters['dashboard/sidebar'];
		},
		hasBackground: function () {
			return this.$store.getters['settings/hasBackground'];
		},
		isTrial: function () {
			return this.$store.getters['user/isTrial'];
		},
		realtimeConnectionState: function () {
			return this.$store.getters['realtimeio/realtimeConnectionState'];
		},
		statusTitle: function () {
			if (this.realtimeConnectionState === 'default') {
				return 'App is trying to connect to the server';
			}
			if (this.realtimeConnectionState === 'connected') {
				return 'App is connected to the server';
			}
			if (this.realtimeConnectionState === 'unavailable') {
				return "App can't connect to the server either due to connectivity issues or conflicts with the client";
			}
			return;
		},
		userTitle: function () {
			return 'Youre on the free plan. Upgrade now and take unlimited annotations.';
		},
		version: function () {
			return this.$store.getters.version;
		},
		avatar: function () {
			return this.$store.getters['user/avatar'];
		},
	},
	methods: {
		gotoHome: function () {
			this.$store.dispatch('dashboard/updateFolder', null);
		},
		onLogout: function () {
			this.$store.dispatch('user/logout');
		},
	},
};
</script>

<style lang="scss">
.c-sidebar {
	position: relative;
	height: 100%;
	width: 100%;

	&__cover {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		background-color: var(--g3);
		border-right: var(--g2) solid 1px;

		&:after {
			pointer-events: none;
		}

		&.active {
			background-color: transparent;
			border-right: none;

			&:after {
				@include overlay(rgb(34 35 37 / 60%));
				mix-blend-mode: overlay;
			}
		}
	}

	&__inner {
		position: relative;
		z-index: 1000;
		height: 100%;
		display: flex;
		flex-direction: column;
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: calc(var(--size-v) * 2) calc(var(--size-h) * 2);
		margin-bottom: calc(var(--size-h) * 4);

		.img-container {
			position: relative;
			display: inline-block;
			width: 24px;
			height: 24px;
			margin-right: 8px;

			img {
				width: 100%;
				height: 100%;
			}
		}

		.logo {
			display: inline-flex;
			justify-content: center;

			&__inner {
				position: relative;
				display: inline-block;

				.img-container {
					width: 32px;
					height: 32px;
				}
			}
		}

		.status {
			position: absolute;
			bottom: -2px;
			right: -2px;
			width: 12px;
			height: 12px;
			border-radius: 50%;
			border: var(--g3) solid 2px;
			background-color: $gray-600;
			&.connected {
				background-color: $green;
			}
			&.unavailable {
				background-color: $red;
			}
		}

		.heading {
			margin-left: 4px;
			display: inline-block;
			> span {
				display: block;
				font-size: var(--font-size-xs);
				font-weight: 500;
				line-height: 1.4;

				&:nth-child(2) {
					opacity: 0.9;
					font-weight: 400;
					font-size: var(--font-size-xs);
					letter-spacing: 0.3px;
				}
			}
		}

		#profile-dropdown__BV_toggle_ {
			background-image: none;
			background-color: rgba(255, 255, 255, 0.05);

			box-shadow: none;

			img {
				width: 24px;
				height: 24px;
				border-radius: 50%;
			}

			.status {
				bottom: -3px;
				right: -3px;
				border-color: var(--g1);
				background-color: var(--warning-color);
			}
		}
	}

	&__body {
		flex-grow: 1;
		overflow-y: auto;

		@include scrollBars();
	}

	&__footer {
		border-top: rgba(255, 255, 255, 0.075) solid 1px;
		padding: 10px;

		a {
			width: 100%;
			text-align: left;
			padding: 0.35rem 5px;
			opacity: 0.8;
			margin-bottom: 2px;
			display: inline-flex;
			align-items: center;
			color: #fff;

			svg {
				margin-right: 4px;
			}
		}
	}

	.btn-toggle {
		display: none;
	}

	@include media-breakpoint-down(sm) {
		.btn-toggle {
			display: inline-flex;
			padding-left: 6px;
			padding-right: 6px;

			svg {
				transition: transform 150ms ease-out;
			}

			&.active {
				svg {
					transform: rotate(180deg);
				}
			}
		}
	}
}
</style>
